<template>
  <div class="service-wrap pt-20 pb-20">
    <div class="container">

      <div class="row">
        <div class="col-xl-3 col-lg-4 col-md-6" v-for="(etudiant, index) in etudiants" :key="index">
          <div class="team-card style1">
            <div 
              class="team-img"
              @click="openModal(etudiant)"
            >
              <!-- Photo ou initiales -->
              <div 
                v-if="etudiant.user?.urlImage"
                :style="{
                  height: '300px',
                  backgroundImage: `url(${getImageUrl(etudiant.user.urlImage)})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  borderRadius: '10px',
                  cursor: 'pointer'
                }"
              ></div>
              <div 
                v-else
                class="no-photo-placeholder"
              >
                <span class="card-initials">
                  {{ getInitials(etudiant.nom, etudiant.prenom) }}
                </span>
              </div>

              <div class="team-overlay">
                <i class="flaticon-eye"></i>
              </div>
            </div>
            <div class="team-info text-center mt-3">
              <h4>{{ etudiant.nom }} {{ etudiant.prenom }}</h4>
              <span class="team-designation">{{ etudiant.filiere || 'Disciple' }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-4">
        <PaginationComponent
          :page="page"
          :totalPages="totalPages"
          :totalElements="totalElements"
          :limit="limit"
          @paginate="handlePaginate"
        />
      </div>
    </div>

    <!-- Modal pour afficher les détails de l'étudiant -->
    <div v-if="showModal && selectedEtudiant" class="photo-modal" @click="closeModal">
      <div class="modal-content" @click.stop>
        <span class="close-btn" @click="closeModal">&times;</span>
        
        <!-- Image ou initiales -->
        <div v-if="selectedEtudiant.user?.urlImage" class="modal-image-container">
          <img :src="getImageUrl(selectedEtudiant.user.urlImage)" :alt="selectedEtudiant.nom" />
        </div>
        <div v-else class="modal-initials-container">
          <div class="initials-circle">
            {{ getInitials(selectedEtudiant.nom, selectedEtudiant.prenom) }}
          </div>
        </div>
        
        <div class="modal-info">
          <h3>{{ selectedEtudiant.nom }} {{ selectedEtudiant.prenom }}</h3>
          <p class="specialite">{{ selectedEtudiant.filiere || 'Disciple' }}</p>
          
          <!-- Informations supplémentaires si disponibles -->
          <div class="details" v-if="selectedEtudiant">
            <p v-if="selectedEtudiant.email"><strong>Email:</strong> {{ selectedEtudiant.email }}</p>
            <p v-if="selectedEtudiant.telephone"><strong>Téléphone:</strong> {{ selectedEtudiant.telephone }}</p>
            <p v-if="selectedEtudiant.matricule"><strong>Matricule:</strong> {{ selectedEtudiant.matricule }}</p>
            <p v-if="selectedEtudiant.dateNaissance"><strong>Date de naissance:</strong> {{ selectedEtudiant.dateNaissance }}</p>
            <p v-if="selectedEtudiant.lieuNaissance"><strong>Lieu de naissance:</strong> {{ selectedEtudiant.lieuNaissance }}</p>
            <p v-if="selectedEtudiant.adresse"><strong>Adresse:</strong> {{ selectedEtudiant.adresse }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import ApiService from "@/services/ApiService";
import { getUrlApiForFiles, error } from "@/utils/utils";
import { defineComponent, ref, onMounted } from "vue";
import PaginationComponent from '@/components/Utilities/Pagination.vue';

interface Etudiant {
  id: number;
  nom: string;
  prenom: string;
  photo: string;
  filiere?: string;
  email?: string;
  telephone?: string;
  matricule?: string;
  dateNaissance?: string;
  lieuNaissance?: string;
  adresse?: string;
  user?:User
}
interface User {
  urlImage?: string
}

export default defineComponent({
  name: 'DisciPle',
  components: { PaginationComponent },
  setup() {
    const etudiants = ref<Array<Etudiant>>([]);
    const isLoading = ref(false);
    const showModal = ref(false);
    const selectedEtudiant = ref<Etudiant | null>(null);

    // PAGINATION
    const page = ref(1);
    const totalPages = ref(0);
    const limit = ref(12);
    const totalElements = ref(0);

    const handlePaginate = ({ page_, limit_ }) => {
      page.value = page_;
      getEtudiants(page_, limit_);
    };

    // Récupérer la liste des étudiants
    function getEtudiants(pageNum = 1, limi = 12) {
      isLoading.value = true;
      return ApiService.get(
        `/etudiants?page=${pageNum}&limit=${limi}`
      )
        .then(({ data }) => {
          etudiants.value = data.data.data;
          totalPages.value = data.data.totalPages;
          limit.value = data.data.limit;
          totalElements.value = data.data.totalElements;
        })
        .catch(({ response }) => {
          error(response?.data?.message || "Erreur de chargement");
          etudiants.value = [];
        })
        .finally(() => {
          isLoading.value = false;
        });
    }
    function getImageUrl(filename?: string) : string {
  if (!filename) return ''
  return `${ApiService.vueInstance.axios.defaults.baseURL?.split("api")[0]}uploads/Users/${filename}`; }

    function openModal(etudiant: Etudiant) {
      selectedEtudiant.value = etudiant;
      showModal.value = true;
    }

    function closeModal() {
      showModal.value = false;
      selectedEtudiant.value = null;
    }

    // Fonction pour obtenir les initiales
    function getInitials(nom: string, prenom: string): string {
      const firstLetter = prenom?.charAt(0)?.toUpperCase() || '';
      const lastLetter = nom?.charAt(0)?.toUpperCase() || '';
      return `${firstLetter}${lastLetter}`;
    }

    onMounted(() => {
      getEtudiants();
    });

    return {
      etudiants,
      isLoading,
      showModal,
      selectedEtudiant,
      getUrlApiForFiles,
      page,
      totalPages,
      limit,
      totalElements,
      handlePaginate,
      openModal,
      closeModal,
      getInitials,
      getImageUrl
    };
  },
});
</script>

<style scoped>
.section-title {
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 10px;
  color: #1a1a1a;
}

.section-subtitle {
  font-size: 16px;
  color: #666;
  margin-bottom: 30px;
}

.team-card {
  transition: transform 0.3s ease;
  margin-bottom: 20px;
}

.team-card:hover {
  transform: translateY(-5px);
}

.team-img {
  position: relative;
  overflow: hidden;
  height: 300px;
  border-radius: 10px;
  cursor: pointer;
}

.team-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 10px;
}

.team-img:hover .team-overlay {
  opacity: 1;
}

.team-overlay i {
  font-size: 32px;
  color: white;
}

.team-info h4 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 5px;
  color: #1a1a1a;
}

.team-designation {
  font-size: 14px;
  color: #666;
}

/* Placeholder sans photo */
.no-photo-placeholder {
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f3f4f6;
  border-radius: 10px;
  border: 2px dashed #d1d5db;
}

.card-initials {
  font-size: 60px;
  font-weight: bold;
  color: #6b7280;
}

/* Modal */
.photo-modal {
  position: fixed;
  z-index: 9999;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  position: relative;
  max-width: 500px; 
  width: 90%;
  max-height: 90vh;
  background: white;
  border-radius: 10px;
  overflow-y: auto;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from { 
    transform: translateY(-50px);
    opacity: 0;
  }
  to { 
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-image-container img {
  width: 100%;
  height: auto;
  max-height: 350px; 
  object-fit: cover;
  display: block;
  border-radius: 10px 10px 0 0;
}

.modal-initials-container {
  display: flex;
  justify-content: center;
  padding: 40px 20px;
  background-color: #f9fafb;
}

.initials-circle {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background-color: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 56px;
  font-weight: bold;
  color: #4b5563;
}

.close-btn {
  position: absolute;
  top: 15px;
  right: 25px;
  color: white;
  font-size: 40px;
  font-weight: bold;
  cursor: pointer;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.5);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition: background 0.3s ease;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.8);
}

.modal-info {
  padding: 25px; 
  text-align: center;
}

.modal-info h3 {
  font-size: 24px; 
  font-weight: 600;
  margin-bottom: 10px;
  color: #1a1a1a;
}

.modal-info .specialite {
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
  font-style: italic;
}

.modal-info .details {
  text-align: left;
  max-width: 400px; 
  margin: 0 auto;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.modal-info .details p {
  font-size: 14px;
  margin-bottom: 10px;
  color: #333;
}

.modal-info .details strong {
  color: #000;
  margin-right: 5px;
}

.section-title {
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 10px;
  color: #1a1a1a;
}

.section-subtitle {
  font-size: 16px;
  color: #666;
  margin-bottom: 30px;
}

.team-card {
  transition: transform 0.3s ease;
  margin-bottom: 20px;
}

.team-card:hover {
  transform: translateY(-5px);
}

.team-img {
  position: relative;
  overflow: hidden;
  height: 300px;
  border-radius: 10px;
  cursor: pointer;
}

.team-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 10px;
}

.team-img:hover .team-overlay {
  opacity: 1;
}

.team-overlay i {
  font-size: 32px;
  color: white;
}

.team-info h4 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 5px;
  color: #1a1a1a;
}

.team-designation {
  font-size: 14px;
  color: #666;
}

/* Placeholder sans photo */
.no-photo-placeholder {
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f3f4f6;
  border-radius: 10px;
  border: 2px dashed #d1d5db;
}

.card-initials {
  font-size: 60px;
  font-weight: bold;
  color: #6b7280;
}

/* Modal */
.photo-modal {
  position: fixed;
  z-index: 9999;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  position: relative;
  max-width: 500px; 
  width: 90%;
  max-height: 90vh;
  background: white;
  border-radius: 10px;
  overflow-y: auto;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from { 
    transform: translateY(-50px);
    opacity: 0;
  }
  to { 
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-image-container img {
  width: 100%;
  height: auto;
  max-height: 350px; 
  object-fit: cover;
  display: block;
  border-radius: 10px 10px 0 0;
}

.modal-initials-container {
  display: flex;
  justify-content: center;
  padding: 40px 20px;
  background-color: #f9fafb;
}

.initials-circle {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background-color: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 56px;
  font-weight: bold;
  color: #4b5563;
}

.close-btn {
  position: absolute;
  top: 15px;
  right: 25px;
  color: white;
  font-size: 40px;
  font-weight: bold;
  cursor: pointer;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.5);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition: background 0.3s ease;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.8);
}

.modal-info {
  padding: 25px; 
  text-align: center;
}

.modal-info h3 {
  font-size: 24px; 
  font-weight: 600;
  margin-bottom: 10px;
  color: #1a1a1a;
}

.modal-info .specialite {
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
  font-style: italic;
}

.modal-info .details {
  text-align: left;
  max-width: 400px; 
  margin: 0 auto;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.modal-info .details p {
  font-size: 14px;
  margin-bottom: 10px;
  color: #333;
}

.modal-info .details strong {
  color: #000;
  margin-right: 5px;
}
</style>